import {
  COLOR_TOKENS,
  TASK_PRIORITIES,
  TASK_PRIORITY_LABELS,
  TASK_TYPES,
  TASK_TYPE_LABELS,
  describeRecurrence,
  normalizeRecurrence,
  recurrencePresets,
  type ColorToken,
  type CreateTaskBody,
  type LeadDetailDTO,
  type RecurrenceEditScope,
  type TaskDTO,
  type TaskPriority,
  type TaskType,
  type UpdateTaskBody,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { CalendarPlus, MapPin, Trash2, X } from 'lucide-react';
import { useId, useState } from 'react';
import { Button, Field, Input, MultiSelect, Select, SidePanel, Switch, Textarea, useConfirm, useToast } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { useLookups } from '../../hooks/useMeta';
import { api, errorMessage, fieldErrors } from '../../lib/api';
import { colorClasses } from '../../lib/colors';
import {
  DEFAULT_MEETING_REMINDER,
  MEETING_DURATION_MINUTES,
  addEnd,
  browserTimeZone,
  changedFields,
  createBody,
  defaultStart,
  defaultTitle,
  entryNoun,
  firstParts,
  initialRepeat,
  mapReminder,
  moveStart,
  rebaseRepeat,
  reminderOptions,
  repeatRule,
  setAllDay,
  valuesFromTask,
  whenAt,
  whenError,
  whenFromWire,
  whenToWire,
  type EntryValues,
  type EntryWhen,
  type RepeatChoice,
} from './entryModel';
import { LeadPicker, type PickedLead } from './LeadPicker';
import { RecurrenceEditor } from './RecurrenceEditor';
import { useRecurrenceScopePrompt } from './recurrenceScope';
import { taskColor } from './taskAppearance';
import { invalidateTaskQueries } from './taskQueries';

/** Prefill for a new entry (e.g. the slot clicked on the calendar). `dueAt: null` = no date. */
export type TaskFormDefaults = {
  dueAt?: string | null;
  endAt?: string | null;
  allDay?: boolean;
  type?: TaskType;
  title?: string;
  description?: string | null;
  assignedTo?: string | null;
  attendeeIds?: string[];
};

export type TaskFormPanelProps = {
  businessId?: string;
  businessName?: string;
  opportunityId?: string;
  contactId?: string;
  defaultType?: TaskType;
  defaults?: TaskFormDefaults;
  task?: TaskDTO;
  onClose: () => void;
  onSaved?: (task: TaskDTO) => void;
};

/** Server field path (first segment) → the control that shows the error; anything else is a toast. */
const ERROR_FIELD: Record<string, string> = {
  type: 'type',
  title: 'title',
  description: 'description',
  contactId: 'contactId',
  dueAt: 'dueAt',
  allDay: 'dueAt',
  endAt: 'endAt',
  location: 'location',
  color: 'color',
  reminderMinutes: 'reminderMinutes',
  priority: 'priority',
  assignedTo: 'assignedTo',
  attendeeIds: 'attendeeIds',
  recurrence: 'recurrence',
};

function initialWhen(type: TaskType, task: TaskDTO | undefined, defaults: TaskFormDefaults | undefined): EntryWhen {
  if (task) return whenFromWire(task);
  const blockMinutes = type === 'meeting' ? MEETING_DURATION_MINUTES : null;
  if (defaults?.dueAt === undefined || (defaults.dueAt === null && type === 'note')) return whenAt(defaultStart(new Date()), blockMinutes);
  const when = whenFromWire({ dueAt: defaults.dueAt, endAt: defaults.endAt ?? null, allDay: defaults.allDay ?? false });
  return blockMinutes !== null && defaults.endAt === undefined && !when.allDay && when.startDate !== '' ? addEnd(when, blockMinutes) : when;
}

/**
 * Create or edit a task / calendar entry: type, title, time (all-day, time block or a point in time, or no date for
 * plain tasks), repeat, lead and contact, location, assignee and guests, reminder, color, priority and notes.
 * Used from the tasks page, the lead profile, the dashboard and the calendar.
 */
export function TaskFormPanel({
  businessId,
  businessName,
  opportunityId,
  contactId,
  defaultType = 'follow_up',
  defaults,
  task,
  onClose,
  onSaved,
}: TaskFormPanelProps) {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { user, can } = useAuth();
  const { meta, lookups } = useLookups();
  const scopePrompt = useRecurrenceScopePrompt();
  const canAssign = can('lead:assign');
  const colorLabelId = useId();

  const firstType = task?.type ?? defaults?.type ?? defaultType;
  const [type, setType] = useState<TaskType>(firstType);
  const [title, setTitle] = useState(task?.title ?? defaults?.title ?? defaultTitle(firstType));
  const [description, setDescription] = useState(task?.description ?? defaults?.description ?? '');
  const [when, setWhen] = useState(() => initialWhen(firstType, task, defaults));
  const [repeat, setRepeat] = useState<RepeatChoice>(() => (task ? initialRepeat(task.recurrence, firstParts(whenFromWire(task))) : { key: 'none' }));
  const [pickedLead, setPickedLead] = useState<PickedLead | null>(null);
  const [contact, setContact] = useState(task?.contactId ?? contactId ?? '');
  const [location, setLocation] = useState(task?.location ?? '');
  const [assignedTo, setAssignedTo] = useState(task ? (task.assignedTo ?? '') : defaults?.assignedTo !== undefined ? (defaults.assignedTo ?? '') : (user?.id ?? ''));
  const [attendeeIds, setAttendeeIds] = useState<string[]>(task?.attendeeIds ?? defaults?.attendeeIds ?? []);
  const [reminder, setReminder] = useState<number | null>(task ? task.reminderMinutes : firstType === 'meeting' ? DEFAULT_MEETING_REMINDER : null);
  const [color, setColor] = useState<ColorToken | null>(task?.color ?? null);
  const [priority, setPriority] = useState<TaskPriority>(task?.priority ?? 'normal');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // An entry on a lead stays there; a new or personal entry may get one from the caller or the picker.
  const leadFixed = businessId !== undefined || Boolean(task?.businessId);
  const leadId = task?.businessId ?? businessId ?? pickedLead?.businessId ?? null;
  // A personal entry being put on a lead sends it along with the other changes.
  const attachLead = task && !task.businessId && pickedLead ? { businessId: pickedLead.businessId } : {};
  const { data: leadDetail } = useQuery({
    queryKey: ['leads', leadId],
    queryFn: ({ signal }) => api.get<LeadDetailDTO>(`/api/leads/${leadId}`, { signal }),
    enabled: leadId !== null,
  });
  const leadName = task?.businessName ?? businessName ?? leadDetail?.name ?? null;

  const noun = entryNoun(type);
  const dated = when.startDate !== '';
  const first = firstParts(when);
  const rule = dated ? repeatRule(repeat) : null;
  const dateRequired = type === 'note' || rule !== null;
  const blockMinutes = type === 'meeting' ? MEETING_DURATION_MINUTES : null;

  const activeUsers = (meta?.users ?? []).filter((candidate) => candidate.isActive);
  const assigneeOptions = [
    { value: '', label: 'Unassigned' },
    ...activeUsers.map((candidate) => ({ value: candidate.id, label: candidate.name })),
    // Keep a deactivated assignee visible rather than silently showing "Unassigned".
    ...(assignedTo && !activeUsers.some((candidate) => candidate.id === assignedTo)
      ? [{ value: assignedTo, label: lookups.userById(assignedTo)?.name ?? 'Former user' }]
      : []),
  ];
  const guestOptions = [
    ...activeUsers.filter((candidate) => candidate.id !== assignedTo).map((candidate) => ({ value: candidate.id, label: candidate.name })),
    ...attendeeIds
      .filter((guestId) => !activeUsers.some((candidate) => candidate.id === guestId))
      .map((guestId) => ({ value: guestId, label: lookups.userById(guestId)?.name ?? 'Former user' })),
  ];
  const guestNames = attendeeIds.map((guestId) => lookups.userById(guestId)?.name).filter(Boolean);

  const contactOptions = (leadDetail?.contacts ?? []).map((item) => ({ value: item.id, label: item.displayName }));
  if (task?.contactId && !contactOptions.some((option) => option.value === task.contactId)) {
    contactOptions.push({ value: task.contactId, label: task.contactName ?? 'Contact' });
  }
  const address = leadDetail
    ? [leadDetail.addressLine1, leadDetail.addressLine2, leadDetail.city, leadDetail.region, leadDetail.postalCode].filter(Boolean).join(', ')
    : '';

  const presets = first ? recurrencePresets(first) : [];

  const changeType = (next: TaskType) => {
    if (!task && title === defaultTitle(type)) setTitle(defaultTitle(next));
    if (!dated && next === 'note') setWhen(whenAt(defaultStart(new Date()), null));
    if (!task && next === 'meeting' && dated) {
      if (!when.allDay && !when.hasEnd) setWhen(addEnd(when, MEETING_DURATION_MINUTES));
      if (reminder === null) setReminder(DEFAULT_MEETING_REMINDER);
    }
    setType(next);
  };

  const changeStart = (startDate: string, startTime: string) => {
    const next = moveStart(when, startDate, startTime);
    setWhen(next);
    setRepeat((current) => rebaseRepeat(current, firstParts(next)));
  };

  const changeAllDay = (allDay: boolean) => {
    setWhen(setAllDay(when, allDay, blockMinutes));
    setReminder((current) => mapReminder(current, allDay));
  };

  const changeRepeat = (key: string) => {
    if (key === 'none') return setRepeat({ key: 'none' });
    if (key === 'custom') {
      if (!first) return;
      return setRepeat({ key: 'custom', rule: repeatRule(repeat) ?? normalizeRecurrence({ freq: 'weekly' }, first) });
    }
    const preset = presets.find((candidate) => candidate.key === key);
    if (preset) setRepeat({ key: preset.key, rule: preset.rule });
  };

  const changeAssignee = (next: string) => {
    setAssignedTo(next);
    setAttendeeIds((current) => current.filter((guestId) => guestId !== next));
  };

  const currentValues = (): EntryValues => {
    const wire = whenToWire(when);
    return {
      type,
      title,
      description: description.trim() || null,
      contactId: leadId ? contact || null : null,
      ...wire,
      location: location.trim() || null,
      color,
      reminderMinutes: wire.dueAt ? reminder : null,
      priority,
      assignedTo: assignedTo || null,
      attendeeIds,
      recurrence: wire.dueAt ? rule : null,
    };
  };

  const save = useMutation({
    mutationFn: (request: { create: CreateTaskBody } | { update: UpdateTaskBody }) =>
      'create' in request ? api.post<TaskDTO>('/api/tasks', request.create) : api.patch<TaskDTO>(`/api/tasks/${task?.id}`, request.update),
    onSuccess: async (saved) => {
      const label = noun.charAt(0).toUpperCase() + noun.slice(1);
      toast.success(task ? `${label} updated` : `${label} created`);
      await invalidateTaskQueries(queryClient, saved.businessId ?? leadId);
      onSaved?.(saved);
      onClose();
    },
    onError: (error) => {
      const byField: Record<string, string> = {};
      for (const [path, message] of Object.entries(fieldErrors(error))) {
        const field = ERROR_FIELD[path.split('.')[0] ?? path];
        if (field) byField[field] ??= message;
      }
      setErrors(byField);
      if (Object.keys(byField).length === 0) toast.error(errorMessage(error));
    },
  });

  const remove = useMutation({
    mutationFn: (scope: RecurrenceEditScope) => api.delete<void>(`/api/tasks/${task?.id}?scope=${scope}`),
    onSuccess: async () => {
      toast.success(`${noun.charAt(0).toUpperCase() + noun.slice(1)} deleted`);
      await invalidateTaskQueries(queryClient, task?.businessId);
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const submit = async () => {
    const problems: Record<string, string> = {};
    if (title.trim() === '') problems.title = 'Enter a title';
    const timeProblem = whenError(when);
    if (timeProblem) problems[timeProblem.field === 'start' ? 'dueAt' : 'endAt'] = timeProblem.message;
    if (!dated && type === 'note') problems.dueAt = 'A note needs a date';
    setErrors(problems);
    if (Object.keys(problems).length > 0) return;

    const values = currentValues();
    if (!task) {
      save.mutate({
        create: createBody(values, { businessId: leadId, opportunityId: opportunityId ?? null, canAssign, timeZone: browserTimeZone() }),
      });
      return;
    }
    const patch = { ...changedFields(valuesFromTask(task), values), ...attachLead };
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    let scope: RecurrenceEditScope = 'this';
    if (task.seriesId) {
      // A new repeat rule reshapes the series, so it cannot apply to this occurrence alone.
      const answer = await scopePrompt.ask('edit', { allowThis: !('recurrence' in patch) });
      if (!answer) return;
      scope = answer;
    }
    save.mutate({ update: { ...patch, scope, timeZone: browserTimeZone() } });
  };

  const onDelete = async () => {
    if (!task) return;
    if (task.seriesId) {
      const scope = await scopePrompt.ask('delete');
      if (scope) remove.mutate(scope);
      return;
    }
    const confirmed = await confirm({ title: `Delete this ${noun}?`, message: `“${task.title}” will be removed.`, confirmLabel: 'Delete', danger: true });
    if (confirmed) remove.mutate('this');
  };

  return (
    <SidePanel
      open
      onClose={onClose}
      title={`${task ? 'Edit' : 'New'} ${noun}`}
      width="lg"
      footer={
        <div className="flex items-center justify-between gap-2">
          <div>
            {task && can('lead:write') && (
              <Button variant="ghost" icon={Trash2} loading={remove.isPending} onClick={() => void onDelete()}>
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" loading={save.isPending} disabled={title.trim() === ''} onClick={() => void submit()}>
              {task ? 'Save' : `Add ${noun}`}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-[12rem_minmax(0,1fr)]">
          <Field label="Type" error={errors.type}>
            <Select value={type} onChange={(event) => changeType(event.target.value as TaskType)} options={TASK_TYPES.map((value) => ({ value, label: TASK_TYPE_LABELS[value] }))} />
          </Field>
          <Field label="Title" required error={errors.title}>
            <Input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Call about the second oven" />
          </Field>
        </div>

        <fieldset className="space-y-3 rounded-md border border-slate-200 p-3">
          <legend className="px-1 text-[13px] font-medium text-slate-700">When</legend>
          {dated ? (
            <>
              <div className="flex items-center justify-between gap-2">
                <Switch size="sm" label="All day" checked={when.allDay} onChange={changeAllDay} />
                {!dateRequired && (
                  <Button size="sm" variant="ghost" icon={X} onClick={() => setWhen({ ...when, startDate: '' })}>
                    No date
                  </Button>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={when.allDay ? 'First day' : 'Start date'} error={errors.dueAt}>
                  <Input type="date" value={when.startDate} onChange={(event) => changeStart(event.target.value, when.startTime)} />
                </Field>
                {!when.allDay && (
                  <Field label="Start time">
                    <Input type="time" value={when.startTime} onChange={(event) => changeStart(when.startDate, event.target.value)} />
                  </Field>
                )}
              </div>
              {when.allDay ? (
                <Field label="Last day" error={errors.endAt} className="sm:w-1/2 sm:pr-1.5">
                  <Input type="date" min={when.startDate} value={when.endDate} onChange={(event) => setWhen({ ...when, endDate: event.target.value })} />
                </Field>
              ) : when.hasEnd ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="End date" error={errors.endAt}>
                    <Input type="date" min={when.startDate} value={when.endDate} onChange={(event) => setWhen({ ...when, endDate: event.target.value })} />
                  </Field>
                  <Field
                    label="End time"
                    labelAside={
                      <button type="button" className="text-xs text-slate-500 hover:text-ember-700" onClick={() => setWhen({ ...when, hasEnd: false })}>
                        Remove end
                      </button>
                    }
                  >
                    <Input type="time" value={when.endTime} onChange={(event) => setWhen({ ...when, endTime: event.target.value })} />
                  </Field>
                </div>
              ) : (
                <Button size="sm" variant="ghost" icon={CalendarPlus} onClick={() => setWhen(addEnd(when, blockMinutes ?? 30))}>
                  Add end time
                </Button>
              )}
            </>
          ) : (
            <div className="flex items-center justify-between gap-2 text-[13px] text-slate-600">
              <span>No date — it stays in the "No date" list until you schedule it.</span>
              <Button size="sm" icon={CalendarPlus} onClick={() => setWhen(whenAt(defaultStart(new Date()), blockMinutes))}>
                Add date
              </Button>
            </div>
          )}
          {errors.dueAt && !dated && <p className="text-xs text-red-600">{errors.dueAt}</p>}
        </fieldset>

        <div className="space-y-2">
          <Field
            label="Repeat"
            error={errors.recurrence}
            hint={dated ? undefined : 'Pick a date to repeat.'}
          >
            <Select
              value={dated ? repeat.key : 'none'}
              disabled={!dated}
              onChange={(event) => changeRepeat(event.target.value)}
              options={[
                { value: 'none', label: 'Does not repeat' },
                ...presets.map((preset) => ({ value: preset.key, label: preset.label })),
                { value: 'custom', label: 'Custom…' },
              ]}
            />
          </Field>
          {dated && repeat.key === 'custom' && rule && first && (
            <RecurrenceEditor rule={rule} first={first} onChange={(next) => setRepeat({ key: 'custom', rule: next })} />
          )}
          {rule && first && <p className="text-xs text-slate-600">{describeRecurrence(rule, first)}</p>}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Lead">
            {leadFixed ? (
              <p className="flex h-8 items-center truncate text-[13px] text-slate-800">{leadName ?? 'Lead'}</p>
            ) : (
              <LeadPicker
                value={pickedLead}
                name={leadDetail?.name}
                onChange={(next) => {
                  setPickedLead(next);
                  setContact(next?.contactId ?? '');
                }}
              />
            )}
          </Field>
          {leadId && (
            <Field label="Contact" error={errors.contactId}>
              <Select value={contact} onChange={(event) => setContact(event.target.value)} placeholder="No contact" options={contactOptions} />
            </Field>
          )}
        </div>

        <Field
          label="Location"
          error={errors.location}
          labelAside={
            location.trim() === '' && address !== '' ? (
              <button type="button" className="flex items-center gap-1 text-xs text-slate-500 hover:text-ember-700" onClick={() => setLocation(address)}>
                <MapPin className="size-3" aria-hidden />
                Use the restaurant's address
              </button>
            ) : undefined
          }
        >
          <Input value={location} onChange={(event) => setLocation(event.target.value)} placeholder="At the restaurant, on the phone, video link…" />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Assigned to" error={errors.assignedTo}>
            {canAssign ? (
              <Select value={assignedTo} onChange={(event) => changeAssignee(event.target.value)} options={assigneeOptions} />
            ) : (
              <p className="flex h-8 items-center text-[13px] text-slate-800">
                {!assignedTo || assignedTo === user?.id ? 'Assigned to you' : `Assigned to ${lookups.userById(assignedTo)?.name ?? 'a teammate'}`}
              </p>
            )}
          </Field>
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] font-medium text-slate-700" aria-hidden>
              Guests
            </span>
            <MultiSelect size="md" label="Guests" options={guestOptions} value={attendeeIds} onChange={setAttendeeIds} searchable />
            {errors.attendeeIds ? (
              <p className="text-xs text-red-600">{errors.attendeeIds}</p>
            ) : guestNames.length > 0 ? (
              <p className="text-xs text-slate-500">{guestNames.join(', ')}</p>
            ) : null}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Reminder" error={errors.reminderMinutes} hint={dated ? undefined : 'Pick a date to get a reminder.'}>
            <Select
              value={dated && reminder !== null ? String(reminder) : ''}
              disabled={!dated}
              onChange={(event) => setReminder(event.target.value === '' ? null : Number(event.target.value))}
              options={reminderOptions(when.allDay, dated ? reminder : null)}
            />
          </Field>
          {type !== 'note' && (
            <Field label="Priority" error={errors.priority}>
              <Select value={priority} onChange={(event) => setPriority(event.target.value as TaskPriority)} options={TASK_PRIORITIES.map((value) => ({ value, label: TASK_PRIORITY_LABELS[value] }))} />
            </Field>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium text-slate-700" id={colorLabelId}>
            Color
          </span>
          <div role="radiogroup" aria-labelledby={colorLabelId} className="flex flex-wrap items-center gap-1.5">
            <ColorSwatch token={taskColor({ type, color: null })} label="Type color" selected={color === null} onSelect={() => setColor(null)} wide />
            {COLOR_TOKENS.map((token) => (
              <ColorSwatch key={token} token={token} label={token.charAt(0).toUpperCase() + token.slice(1)} selected={color === token} onSelect={() => setColor(token)} />
            ))}
          </div>
        </div>

        <Field label="Notes" error={errors.description}>
          <Textarea rows={4} value={description} onChange={(event) => setDescription(event.target.value)} />
        </Field>
      </div>
      {scopePrompt.element}
    </SidePanel>
  );
}

function ColorSwatch({ token, label, selected, onSelect, wide }: { token: ColorToken; label: string; selected: boolean; onSelect: () => void; wide?: boolean }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={label}
      title={label}
      onClick={onSelect}
      className={clsx(
        'flex h-7 items-center gap-1.5 rounded-full transition-shadow focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember-500',
        wide ? 'border border-slate-200 px-2 text-xs text-slate-700' : 'w-7 justify-center',
        selected && 'ring-2 ring-slate-900 ring-offset-1 ring-offset-surface',
      )}
    >
      <span aria-hidden className={clsx('size-4 rounded-full', colorClasses(token).dot)} />
      {wide && label}
    </button>
  );
}
